import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from 'react-redux'
import axios from "axios"
import { Col, Grid, Row, DropdownButton, MenuItem } from "react-bootstrap"

import getServerURL from "../../../../functions/getServerURL"
import LoadingAnimation from '../../../../reuseable-components/LoadingAnimation'
import { handleErrorFetchingData } from "../../../../functions/errorHandling"
import MetricsFilterSidebar from "./MetricsFilterSidebar"
import MetricsList from "./MetricsList"
import PaginationButtons from "../../../../reuseable-components/PaginationButtons"
import { getUsers } from "../../../login/reducers/manageLogin"


export default function AssignmentMetricsContainer() {
  const dispatch = useDispatch()

  // STATE & EFFECTS
  const [assignmentMetrics, setAssignmentMetrics] = useState([])
  const [allAssignmentMetrics, setAllAssignmentMetrics] = useState([]) // <-- full dataset
  const [functionalGroups, setFunctionalGroups] = useState([])
  const [assessments, setAssessments] = useState([])
  const users = useSelector(state => state.manageLogin.genesisUsers)

  const [fetchingMetrics, setFetchingMetrics] = useState(false)
  // const [fetchingFunctionalGroups, setFetchingFunctionalGroups] = useState(false)
  // const [fetchingAssessments, setFetchingAssessments] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [numberOfPages, setNumberOfPages] = useState(0)
  const [offset, setOffset] = useState(0)
  const [entriesPerPage, setEntriesPerPage] = useState(50)

  const [sortMetricsBy, setSortMetricsBy] = useState("assessment")
  const [metricsFilters, setMetricsFilters] = useState({
    searchTerm: "",
    selectedUsers: [],
    selectedFunctionalGroups: [],
    selectedAssessments: [],
    selectedAttemptStatuses: [],
    selectedVersions: []
  })

  useEffect(() => {
    getAssignmentMetrics(1, sortMetricsBy)
    getAllAssignmentMetrics() // <-- fetch full set for versions
    getFunctionalGroups()
    getAssessments()

    if (!users.length) {
      dispatch(getUsers())
    }
  }, [])


  // REQUESTS
  // Build minimal filter payload to avoid exceeding URL length limits.
  // Only include the fields the backend actually uses for filtering.
  const buildApiFilters = (filters) => ({
    search_term: filters.searchTerm || "",
    selected_users: (filters.selectedUsers || []).map(u => ({ first_name: u.first_name, last_name: u.last_name })),
    selected_functional_groups: (filters.selectedFunctionalGroups || []).map(g => ({ name: g.name })),
    selected_assessments: (filters.selectedAssessments || []).map(a => ({ name: a.name })),
    selected_attempt_statuses: filters.selectedAttemptStatuses || [],
    selected_published_statuses: filters.selectedPublishedStatuses || [],
    selected_versions: filters.selectedVersions || []
  })

  const getAssignmentMetrics = (page = 1, sortBy = null, filters = null) => {
    setFetchingMetrics(true)

    sortBy = sortBy || sortMetricsBy
    filters = filters || buildApiFilters(metricsFilters)

    axios.get(`${getServerURL()}assignments/user_assignment_metrics/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
      params: {
        sort_by: sortBy,
        filters,
      }
    }).then(response => {
      setAssignmentMetrics(response.data.metrics)
      setNumberOfPages(Math.ceil(response.data.count / entriesPerPage))
      setCurrentPage(1)
      setOffset(0)
    }).catch(error => {
      handleErrorFetchingData(error)
    }).finally(() => {
      setFetchingMetrics(false)
    })
  };

  // New function to fetch the full set of metrics for version options
  const getAllAssignmentMetrics = () => {
    axios.get(`${getServerURL()}assignments/user_assignment_metrics/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
    }).then(response => {
      setAllAssignmentMetrics(response.data.metrics)
    }).catch(error => {
      handleErrorFetchingData(error)
    })
  };

  const getFunctionalGroups = () => {
    // setFetchingFunctionalGroups(true)

    axios.get(`${getServerURL()}functional-groups/`, {
      params: { "is_active": true, "ordering": "name" },
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
    }).then(response => {
      setFunctionalGroups(response.data)
    }).catch(error => {
      handleErrorFetchingData(error)
    }).finally(() => {
      // setFetchingFunctionalGroups(false)
    })
  }

  const getAssessments = () => {
    // setFetchingAssessments(true)

    axios.get(`${getServerURL()}assessments/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
    }).then(response => {
      setAssessments(response.data)
    }).catch(error => {
      handleErrorFetchingData(error)
    }).finally(() => {
      // setFetchingAssessments(false)
    })
  }


  // FUNCTIONS
  const handleSortMetricsBy = (sortBy) => {
    setSortMetricsBy(sortBy)
    getAssignmentMetrics(1, sortBy)
  }

  const handleSearchSubmit = () => {
    getAssignmentMetrics(1)
  }

  const handleFilterChange = (filterType, value) => {
    const updatedFilters = {
      ...metricsFilters,
      [filterType]: value
    }
    setMetricsFilters(updatedFilters)

    if (filterType !== 'searchTerm') {
      const filters = buildApiFilters(updatedFilters)
      getAssignmentMetrics(1, sortMetricsBy, filters)
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    setOffset(entriesPerPage * (newPage - 1))
  }

  const paginateMetrics = () => {
    const result = assignmentMetrics.slice(offset, (offset + entriesPerPage))
    return result
  }

  const handleDueDateUpdated = () => {
    // Refresh the metrics after due date is updated
    getAssignmentMetrics(currentPage, sortMetricsBy)
  }

  const renderResultsPerPageDropdown = () => {
    const options = [25, 50, 100]
    return (
      <DropdownButton
        title={`${entriesPerPage} per page`}
        id="metrics-results-dropdown"
        onSelect={(e) => {
          setNumberOfPages(Math.ceil(assignmentMetrics.length / e))
          setEntriesPerPage(e)
          setCurrentPage(1)
          setOffset(0)
        }}
      >
        {options.map((option, idx) => <MenuItem eventKey={option} key={idx} active={entriesPerPage === option ? true : false}>{option}</MenuItem>)}
      </DropdownButton>
    )
  }


  // RENDER
  return (
    <div id="AssignmentMetrics">
      <div>
        <Grid>
          <Row>
            <Col xs={12} md={3}>
              <MetricsFilterSidebar
                sortMetricsBy={ sortMetricsBy }
                handleSortMetricsBy={ handleSortMetricsBy }
                metricsFilters={ metricsFilters }
                allAssignmentMetrics={ allAssignmentMetrics }
                handleFilterChange={ handleFilterChange }
                handleSearchSubmit={ handleSearchSubmit }
                users={ users }
                functionalGroups={ functionalGroups }
                assessments={ assessments }
              />
            </Col>
            <Col xs={12} sm={12} md={9} lg={9}>
              {/* { numberOfPages > 1 && */}
                <center>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px'
                  }}>
                    <PaginationButtons
                      onClick={ handlePageChange }
                      numberOfPages={ numberOfPages }
                      currentPage={ currentPage }
                      buttonsPerPage={ 5 }
                      disabled={ fetchingMetrics }
                    />
                    {renderResultsPerPageDropdown()}
                  </div>
                </center>
              {/* } */}
              { fetchingMetrics ?
                <LoadingAnimation />
              :
                <MetricsList
                  assignmentMetrics={ paginateMetrics() }
                  onDueDateUpdated={ handleDueDateUpdated }
                />
              }
            </Col>
          </Row>
        </Grid>
      </div>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/AssignmentMetricsContainer/index.js